/**
 * Popup content only (EMS-10154) — one entry per data row parsed from an
 * uploaded Analysis file. Field names match the legacy ContributionTab.tsx
 * grid verbatim (naming reuse only, per spec.md FR-004/data-model.md) so the
 * eventual `GET :documentId/rows` response can map 1:1 without renaming.
 */
export interface ParsedContributionRow {
  // Index signature so this satisfies ag-grid's `Grid<DataType extends
  // Record<string, unknown>>` constraint (components/Grid/Grid.tsx) — every
  // field below is a string, so it's compatible.
  readonly [key: string]: string
  readonly Relationship: string
  readonly ContributionAmountOrPercentage: string
  readonly PriceMetric: string
  readonly MetalType: string
  readonly MinAge: string
  readonly MaxAge: string
  readonly State: string
  readonly RatingArea: string
  readonly EmploymentStatus: string
  readonly ExemptStatus: string
  readonly ContributionGroup: string
  readonly PlanYear: string
  readonly BenefitType: string
  readonly ContributionType: string
}

export const MIN_OEP_YEAR = 2000

export type OepTargetFileJobStatus =
  | 'QUEUED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export interface OepTargetFileJob {
  readonly id: string
  readonly fileName: string
  readonly status: OepTargetFileJobStatus
  readonly requestedAt: string
  // Display name of whoever requested the job; null when the backend has no
  // actor recorded for the row.
  readonly requestedBy: string | null
  readonly generatedAt: string | null
  // Analysis document this job was generated from, when the backend records it.
  readonly documentId?: string | null
  // EMS-11070: the generated ABP-5 file's own document id (null until the job
  // completes) and whether it is the current file. Only on by-document jobs.
  readonly generatedDocumentId?: string | null
  readonly isCurrent?: boolean
}

export type OepUploadState =
  | 'idle'
  | 'presigning'
  | 'uploading'
  | 'saving'
  | 'success'
  | 'error'

/** UI toggle key → the `customer_configuration_value` DB key it maps to. */
export type OepToggleKey =
  | 'showOpenEnrollmentTab'
  | 'showPlansSubTab'
  | 'showPeopleSubTab'

export const OEP_CONFIG_KEYS: Readonly<Record<OepToggleKey, string>> = {
  showOpenEnrollmentTab: 'show_open_enrollment_tab',
  showPlansSubTab: 'show_plans_subtab',
  showPeopleSubTab: 'show_people_subtab',
}

/** The master toggle gates the two dependent sub-tab toggles. */
export const OEP_MASTER_KEY: OepToggleKey = 'showOpenEnrollmentTab'

export type OepToggleState = Readonly<Record<OepToggleKey, boolean>>
